/**
 * Nota de partida (GDD, seção 6): cada atleta recebe uma nota de 0 a 10, calculada pela função da posição.
 * Ela vai ser a principal fonte de XP na carreira (item 5.x); no item 4.5 aparece na tela de resultado.
 */
import type { Position } from "./rules.js";

/** Números da partida de um atleta. */
export interface MatchStatLine {
  gols: number;
  arremessos: number;
  passes: number;
  passesCertos: number;
  /** Roubos de bola que ele fez. */
  roubos: number;
  defesas: number;
  rebatidas: number;
  /** Adversários atordoados por Petardo que ele rebateu. */
  petardosAcertados: number;
  /** Vezes em que ele foi atordoado. */
  atordoado: number;
  capturas: number;
  faltas: number;
  /** Quedas no Mergulho. */
  quedas: number;
  /** Ticks do caçador perto da Faísca. */
  faiscaTicks: number;
}

export interface RatingContext {
  /** Resultado do time do atleta. */
  result: "vitoria" | "empate" | "derrota";
  /** Gols que o time dele sofreu (pesa para o goleiro). */
  conceded: number;
}

export const RATING = {
  base: 6,
  min: 0,
  max: 10,
  /** Distância da Faísca que conta como "perto" para o caçador (m). */
  sparkNearMeters: 12,
  result: { vitoria: 0.5, empate: 0, derrota: -0.5 },
  /** Cada falta cometida. */
  foul: -0.4,
  /** Pesos por posição: o que cada função faz de bom. */
  weights: {
    artilheiro: { gols: 0.8, passesCertos: 0.12, roubos: 0.25, arremessosPerdidos: -0.05 },
    goleiro: { defesas: 0.5, golSofrido: -0.15, passesCertos: 0.1, roubos: 0.2 },
    batedor: { petardosAcertados: 0.45, rebatidas: 0.08, roubos: 0.1 },
    cacador: { captura: 2.5, minutoPertoDaFaisca: 0.6, quedas: -0.6 },
  },
} as const;

const TICKS_PER_MINUTE = 30 * 60;

/** Nota de 0 a 10, com uma casa decimal. */
export function matchRating(role: Position, s: MatchStatLine, ctx: RatingContext): number {
  let n = RATING.base + RATING.result[ctx.result] + RATING.foul * s.faltas;
  switch (role) {
    case "artilheiro": {
      const w = RATING.weights.artilheiro;
      n +=
        w.gols * s.gols +
        w.passesCertos * s.passesCertos +
        w.roubos * s.roubos +
        w.arremessosPerdidos * Math.max(0, s.arremessos - s.gols);
      break;
    }
    case "goleiro": {
      const w = RATING.weights.goleiro;
      n +=
        w.defesas * s.defesas +
        w.golSofrido * ctx.conceded +
        w.passesCertos * s.passesCertos +
        w.roubos * s.roubos;
      break;
    }
    case "batedor": {
      const w = RATING.weights.batedor;
      n += w.petardosAcertados * s.petardosAcertados + w.rebatidas * s.rebatidas + w.roubos * s.roubos;
      break;
    }
    case "cacador": {
      const w = RATING.weights.cacador;
      n +=
        w.captura * s.capturas +
        w.minutoPertoDaFaisca * (s.faiscaTicks / TICKS_PER_MINUTE) +
        w.quedas * s.quedas;
      break;
    }
  }
  const clamped = Math.min(RATING.max, Math.max(RATING.min, n));
  return Math.round(clamped * 10) / 10;
}
